import { TERRAIN_SOURCE, TerrainMeta } from "./terrain.ts";

/**
 * Binary terrain format v1, all little-endian:
 *
 *   0   4 bytes  magic "DSTR"
 *   4   u32      version (1)
 *   8   u32      metaLength L — UTF-8 JSON (TerrainMeta), space-padded to a multiple of 4
 *   12  L bytes  meta JSON
 *   .   f32[w*h] elevation, metres LN02, row-major (row 0 = north), NaN = nodata
 *   .   u8[w*h]  source per cell: 0 nodata, 1 bathy, 2 land (TERRAIN_SOURCE)
 *
 * Invariant (checked on encode AND decode): source === 0  ⇔  elevation is NaN.
 *
 * Both api (encode) and client (decode) MUST use these functions — never hand-roll.
 */
const MAGIC = 0x52545344; // "DSTR" read as LE u32
const VERSION = 1;

export interface Terrain {
  meta: TerrainMeta;
  elevation: Float32Array;
  source: Uint8Array;
}

function assertCells(elevation: Float32Array, source: Uint8Array): void {
  for (let i = 0; i < source.length; i++) {
    const s = source[i]!;
    if (s > TERRAIN_SOURCE.land) throw new Error(`terrain: bad source ${s} at cell ${i}`);
    if ((s === TERRAIN_SOURCE.nodata) !== Number.isNaN(elevation[i]!)) {
      throw new Error(`terrain: cell ${i} source ${s} disagrees with elevation ${elevation[i]}`);
    }
  }
}

export function encodeTerrain({ meta, elevation, source }: Terrain): ArrayBuffer {
  const parsed = TerrainMeta.parse(meta);
  const cells = parsed.width * parsed.height;
  if (elevation.length !== cells || source.length !== cells) {
    throw new Error(`terrain: expected ${cells} cells, got ${elevation.length}/${source.length}`);
  }
  assertCells(elevation, source);
  let json = JSON.stringify(parsed);
  while (new TextEncoder().encode(json).length % 4 !== 0) json += " ";
  const metaBytes = new TextEncoder().encode(json);

  const buf = new ArrayBuffer(12 + metaBytes.length + cells * 4 + cells);
  const view = new DataView(buf);
  view.setUint32(0, MAGIC, true);
  view.setUint32(4, VERSION, true);
  view.setUint32(8, metaBytes.length, true);
  new Uint8Array(buf, 12, metaBytes.length).set(metaBytes);
  const elevOffset = 12 + metaBytes.length;
  for (let i = 0; i < cells; i++) view.setFloat32(elevOffset + i * 4, elevation[i]!, true);
  new Uint8Array(buf, elevOffset + cells * 4, cells).set(source);
  return buf;
}

/** Accepts a fresh ArrayBuffer or any view (Uint8Array, Node Buffer slice) at any offset. */
export function decodeTerrain(input: ArrayBuffer | ArrayBufferView): Terrain {
  const bytes =
    input instanceof ArrayBuffer
      ? new Uint8Array(input)
      : new Uint8Array(input.buffer, input.byteOffset, input.byteLength);
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  if (bytes.byteLength < 12 || view.getUint32(0, true) !== MAGIC) {
    throw new Error("terrain: bad magic");
  }
  const version = view.getUint32(4, true);
  if (version !== VERSION) throw new Error(`terrain: unsupported version ${version}`);
  const metaLength = view.getUint32(8, true);
  const metaJson = new TextDecoder().decode(bytes.subarray(12, 12 + metaLength));
  const meta = TerrainMeta.parse(JSON.parse(metaJson));
  const cells = meta.width * meta.height;
  const elevOffset = 12 + metaLength;
  if (bytes.byteLength !== elevOffset + cells * 5) {
    throw new Error(`terrain: length ${bytes.byteLength} != ${elevOffset + cells * 5}`);
  }
  const elevation = new Float32Array(cells);
  for (let i = 0; i < cells; i++) elevation[i] = view.getFloat32(elevOffset + i * 4, true);
  const source = bytes.slice(elevOffset + cells * 4);
  assertCells(elevation, source);
  return { meta, elevation, source };
}
